// Documentação completa do schema do banco de dados Altus Food

export interface TableColumn {
  name: string;
  type: string;
  nullable: boolean;
  default: string | null;
  description: string;
}

export interface ForeignKey {
  column: string;
  references: string;
}

export interface RLSPolicy {
  name: string;
  command: "SELECT" | "INSERT" | "UPDATE" | "DELETE" | "ALL";
  using?: string;
  withCheck?: string;
}

export interface TableSchema {
  name: string;
  description: string;
  domain: string;
  columns: TableColumn[];
  foreignKeys: ForeignKey[];
  rlsPolicies: RLSPolicy[];
}

export const DATABASE_DOMAINS = [
  { id: "caixa", name: "Caixa", icon: "Wallet", color: "text-green-500" },
  { id: "pedidos", name: "Pedidos", icon: "ShoppingCart", color: "text-blue-500" },
  { id: "produtos", name: "Produtos", icon: "Package", color: "text-purple-500" },
  { id: "mesas", name: "Mesas/Comandas", icon: "LayoutGrid", color: "text-orange-500" },
  { id: "estoque", name: "Estoque", icon: "Warehouse", color: "text-yellow-500" },
  { id: "financeiro", name: "Financeiro", icon: "DollarSign", color: "text-emerald-500" },
  { id: "fiscal", name: "Fiscal", icon: "Receipt", color: "text-red-500" },
  { id: "usuarios", name: "Usuários", icon: "Users", color: "text-indigo-500" },
  { id: "config", name: "Configurações", icon: "Settings", color: "text-gray-500" },
  { id: "outros", name: "Outros", icon: "MoreHorizontal", color: "text-slate-500" },
];

export const DATABASE_TABLES: TableSchema[] = [
  // ========== CAIXA ==========
  {
    name: "cash_registers",
    description: "Registros de abertura/fechamento de caixa. Cada caixa pertence a um período operacional.",
    domain: "caixa",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "period_id", type: "uuid", nullable: true, default: null, description: "FK para período operacional" },
      { name: "opened_by", type: "uuid", nullable: false, default: null, description: "Usuário que abriu o caixa" },
      { name: "closed_by", type: "uuid", nullable: true, default: null, description: "Usuário que fechou o caixa" },
      { name: "opening_amount", type: "numeric", nullable: false, default: "0", description: "Valor de abertura (fundo de troco)" },
      { name: "closing_amount", type: "numeric", nullable: true, default: null, description: "Valor contado no fechamento" },
      { name: "expected_amount", type: "numeric", nullable: true, default: null, description: "Valor esperado (calculado)" },
      { name: "difference", type: "numeric", nullable: true, default: null, description: "Diferença (sobra/falta)" },
      { name: "status", type: "cash_register_status", nullable: false, default: "'open'", description: "open, closed" },
      { name: "opened_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora abertura" },
      { name: "closed_at", type: "timestamptz", nullable: true, default: null, description: "Data/hora fechamento" },
      { name: "notes", type: "text", nullable: true, default: null, description: "Observações do fechamento" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Criação do registro" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "period_id", references: "operational_periods.id" },
    ],
    rlsPolicies: [
      { name: "Users can view cash registers of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can create cash registers for their restaurant", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can update cash registers of their restaurant", command: "UPDATE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "cash_movements",
    description: "Movimentações de caixa (sangrias, suprimentos, pagamentos). Cada movimento está vinculado a um caixa aberto.",
    domain: "caixa",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "cash_register_id", type: "uuid", nullable: false, default: null, description: "FK para caixa" },
      { name: "type", type: "cash_movement_type", nullable: false, default: null, description: "withdrawal, deposit, payment" },
      { name: "amount", type: "numeric", nullable: false, default: null, description: "Valor da movimentação" },
      { name: "reason", type: "text", nullable: true, default: null, description: "Motivo da movimentação" },
      { name: "performed_by", type: "uuid", nullable: false, default: null, description: "Usuário que realizou" },
      { name: "expense_id", type: "uuid", nullable: true, default: null, description: "FK para despesa (se for pagamento)" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora da movimentação" },
    ],
    foreignKeys: [
      { column: "cash_register_id", references: "cash_registers.id" },
      { column: "expense_id", references: "expenses.id" },
    ],
    rlsPolicies: [
      { name: "Users can view cash movements of their restaurant", command: "SELECT", using: "EXISTS(SELECT 1 FROM cash_registers cr WHERE cr.id = cash_movements.cash_register_id AND user_belongs_to_restaurant(auth.uid(), cr.restaurant_id))" },
      { name: "Users can create cash movements for their restaurant", command: "INSERT", withCheck: "EXISTS(SELECT 1 FROM cash_registers cr WHERE cr.id = cash_movements.cash_register_id AND user_belongs_to_restaurant(auth.uid(), cr.restaurant_id))" },
    ],
  },
  {
    name: "cash_count_details",
    description: "Detalhes da contagem de caixa por denominação (notas e moedas).",
    domain: "caixa",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "cash_register_id", type: "uuid", nullable: false, default: null, description: "FK para caixa" },
      { name: "type", type: "text", nullable: false, default: null, description: "'note' ou 'coin'" },
      { name: "denomination", type: "numeric", nullable: false, default: null, description: "Valor da cédula/moeda (ex: 100, 50, 0.25)" },
      { name: "quantity", type: "integer", nullable: false, default: "0", description: "Quantidade contada" },
      { name: "total", type: "numeric", nullable: true, default: null, description: "denomination * quantity" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora da contagem" },
    ],
    foreignKeys: [
      { column: "cash_register_id", references: "cash_registers.id" },
    ],
    rlsPolicies: [
      { name: "Users can view cash count details of their restaurant", command: "SELECT", using: "EXISTS(SELECT 1 FROM cash_registers cr WHERE cr.id = cash_count_details.cash_register_id AND user_belongs_to_restaurant(auth.uid(), cr.restaurant_id))" },
      { name: "Users can create cash count details for their restaurant", command: "INSERT", withCheck: "EXISTS(SELECT 1 FROM cash_registers cr WHERE cr.id = cash_count_details.cash_register_id AND user_belongs_to_restaurant(auth.uid(), cr.restaurant_id))" },
    ],
  },
  {
    name: "operational_periods",
    description: "Períodos operacionais (dias de trabalho). Agrupa caixas e pedidos de um mesmo dia/turno.",
    domain: "caixa",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "reference_date", type: "date", nullable: false, default: null, description: "Data de referência do período" },
      { name: "status", type: "text", nullable: false, default: "'open'", description: "open, closed" },
      { name: "opened_by", type: "uuid", nullable: false, default: null, description: "Usuário que abriu o período" },
      { name: "closed_by", type: "uuid", nullable: true, default: null, description: "Usuário que fechou o período" },
      { name: "opened_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora abertura" },
      { name: "closed_at", type: "timestamptz", nullable: true, default: null, description: "Data/hora fechamento" },
      { name: "total_orders", type: "integer", nullable: true, default: "0", description: "Total de pedidos no período" },
      { name: "total_sales", type: "numeric", nullable: true, default: "0", description: "Total de vendas no período" },
      { name: "notes", type: "text", nullable: true, default: null, description: "Observações" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Criação do registro" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view periods of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can create periods for their restaurant", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can update periods of their restaurant", command: "UPDATE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },

  // ========== PEDIDOS ==========
  {
    name: "orders",
    description: "Pedidos do sistema. Cada pedido pertence a uma mesa, comanda ou é delivery/balcão.",
    domain: "pedidos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "table_id", type: "uuid", nullable: true, default: null, description: "FK para mesa (se mesa)" },
      { name: "command_id", type: "uuid", nullable: true, default: null, description: "FK para comanda (se comanda)" },
      { name: "customer_id", type: "uuid", nullable: true, default: null, description: "FK para cliente" },
      { name: "period_id", type: "uuid", nullable: true, default: null, description: "FK para período operacional" },
      { name: "order_number", type: "integer", nullable: false, default: null, description: "Número sequencial do pedido" },
      { name: "order_type", type: "text", nullable: false, default: "'table'", description: "table, command, counter, delivery" },
      { name: "status", type: "order_status", nullable: false, default: "'open'", description: "open, preparing, ready, delivered, cancelled" },
      { name: "subtotal", type: "numeric", nullable: false, default: "0", description: "Subtotal dos itens" },
      { name: "discount", type: "numeric", nullable: true, default: "0", description: "Valor do desconto" },
      { name: "service_tax", type: "numeric", nullable: true, default: "0", description: "Taxa de serviço (10%)" },
      { name: "couvert", type: "numeric", nullable: true, default: "0", description: "Valor do couvert" },
      { name: "total", type: "numeric", nullable: false, default: "0", description: "Total final" },
      { name: "people_count", type: "integer", nullable: true, default: null, description: "Número de pessoas" },
      { name: "notes", type: "text", nullable: true, default: null, description: "Observações gerais" },
      { name: "created_by", type: "uuid", nullable: true, default: null, description: "Usuário que criou" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "table_id", references: "tables.id" },
      { column: "command_id", references: "commands.id" },
      { column: "customer_id", references: "customers.id" },
      { column: "period_id", references: "operational_periods.id" },
    ],
    rlsPolicies: [
      { name: "Users can view orders of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can create orders for their restaurant", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can update orders of their restaurant", command: "UPDATE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Public can view orders by table/command", command: "SELECT", using: "true" },
    ],
  },
  {
    name: "order_items",
    description: "Itens dos pedidos (produtos ou combos). Cada item tem preço, quantidade e status de preparo.",
    domain: "pedidos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "order_id", type: "uuid", nullable: false, default: null, description: "FK para pedido" },
      { name: "product_id", type: "uuid", nullable: true, default: null, description: "FK para produto (se produto)" },
      { name: "combo_id", type: "uuid", nullable: true, default: null, description: "FK para combo (se combo)" },
      { name: "item_type", type: "text", nullable: false, default: "'product'", description: "product, combo" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome do item (snapshot)" },
      { name: "quantity", type: "integer", nullable: false, default: "1", description: "Quantidade" },
      { name: "unit_price", type: "numeric", nullable: false, default: null, description: "Preço unitário" },
      { name: "total_price", type: "numeric", nullable: false, default: null, description: "quantity * unit_price" },
      { name: "notes", type: "text", nullable: true, default: null, description: "Observações do item" },
      { name: "status", type: "text", nullable: false, default: "'pending'", description: "pending, preparing, ready, delivered, cancelled" },
      { name: "sent_to_kitchen", type: "boolean", nullable: false, default: "false", description: "Foi enviado para produção?" },
      { name: "sent_at", type: "timestamptz", nullable: true, default: null, description: "Data/hora do envio" },
      { name: "production_location_id", type: "uuid", nullable: true, default: null, description: "FK para local de produção" },
      { name: "waiter_session_id", type: "uuid", nullable: true, default: null, description: "FK para sessão do garçom" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "order_id", references: "orders.id" },
      { column: "product_id", references: "products.id" },
      { column: "combo_id", references: "combos.id" },
      { column: "production_location_id", references: "production_locations.id" },
      { column: "waiter_session_id", references: "waiter_sessions.id" },
    ],
    rlsPolicies: [
      { name: "Users can view order items of their restaurant", command: "SELECT", using: "EXISTS(SELECT 1 FROM orders o WHERE o.id = order_items.order_id AND user_belongs_to_restaurant(auth.uid(), o.restaurant_id))" },
      { name: "Users can manage order items", command: "ALL", using: "EXISTS(SELECT 1 FROM orders o WHERE o.id = order_items.order_id AND user_belongs_to_restaurant(auth.uid(), o.restaurant_id))" },
      { name: "Public can view order items", command: "SELECT", using: "true" },
    ],
  },
  {
    name: "order_item_addons",
    description: "Adicionais dos itens de pedido. Permite customização de produtos.",
    domain: "pedidos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "order_item_id", type: "uuid", nullable: false, default: null, description: "FK para item do pedido" },
      { name: "addon_id", type: "uuid", nullable: false, default: null, description: "FK para adicional (product)" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome do adicional (snapshot)" },
      { name: "quantity", type: "integer", nullable: false, default: "1", description: "Quantidade" },
      { name: "unit_price", type: "numeric", nullable: false, default: null, description: "Preço unitário" },
      { name: "total_price", type: "numeric", nullable: false, default: null, description: "quantity * unit_price" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
    ],
    foreignKeys: [
      { column: "order_item_id", references: "order_items.id" },
      { column: "addon_id", references: "products.id" },
    ],
    rlsPolicies: [
      { name: "Users can view addons of their restaurant", command: "SELECT", using: "true" },
      { name: "Users can manage addons", command: "ALL", using: "true" },
    ],
  },
  {
    name: "order_item_cancellations",
    description: "Registro de cancelamentos de itens com motivo e valores.",
    domain: "pedidos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "order_item_id", type: "uuid", nullable: false, default: null, description: "FK para item cancelado" },
      { name: "order_id", type: "uuid", nullable: false, default: null, description: "FK para pedido" },
      { name: "product_id", type: "uuid", nullable: true, default: null, description: "FK para produto" },
      { name: "combo_id", type: "uuid", nullable: true, default: null, description: "FK para combo" },
      { name: "quantity", type: "integer", nullable: false, default: null, description: "Quantidade cancelada" },
      { name: "unit_price", type: "numeric", nullable: false, default: null, description: "Preço unitário" },
      { name: "total_price", type: "numeric", nullable: false, default: null, description: "Valor total cancelado" },
      { name: "reason", type: "text", nullable: true, default: null, description: "Motivo do cancelamento" },
      { name: "cancelled_by", type: "uuid", nullable: false, default: null, description: "Usuário que cancelou" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora do cancelamento" },
    ],
    foreignKeys: [
      { column: "order_item_id", references: "order_items.id" },
      { column: "order_id", references: "orders.id" },
      { column: "product_id", references: "products.id" },
      { column: "combo_id", references: "combos.id" },
    ],
    rlsPolicies: [
      { name: "Users can view cancellations of their restaurant", command: "SELECT", using: "true" },
      { name: "Users can create cancellations", command: "INSERT", withCheck: "true" },
    ],
  },
  {
    name: "partial_payments",
    description: "Pagamentos parciais de pedidos. Permite dividir conta em múltiplas formas.",
    domain: "pedidos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "order_id", type: "uuid", nullable: false, default: null, description: "FK para pedido" },
      { name: "payment_method_id", type: "uuid", nullable: false, default: null, description: "FK para forma de pagamento" },
      { name: "amount", type: "numeric", nullable: false, default: null, description: "Valor pago" },
      { name: "received_by", type: "uuid", nullable: false, default: null, description: "Usuário que recebeu" },
      { name: "notes", type: "text", nullable: true, default: null, description: "Observações" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora do pagamento" },
    ],
    foreignKeys: [
      { column: "order_id", references: "orders.id" },
      { column: "payment_method_id", references: "payment_methods.id" },
    ],
    rlsPolicies: [
      { name: "Users can view partial payments", command: "SELECT", using: "true" },
      { name: "Users can create partial payments", command: "INSERT", withCheck: "true" },
    ],
  },

  // ========== PRODUTOS ==========
  {
    name: "products",
    description: "Produtos do cardápio. Inclui produtos simples, adicionais e itens de combo.",
    domain: "produtos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "category_id", type: "uuid", nullable: true, default: null, description: "FK para categoria" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome do produto" },
      { name: "description", type: "text", nullable: true, default: null, description: "Descrição" },
      { name: "price", type: "numeric", nullable: false, default: "0", description: "Preço de venda" },
      { name: "cost", type: "numeric", nullable: true, default: "0", description: "Custo (CMV)" },
      { name: "image_url", type: "text", nullable: true, default: null, description: "URL da imagem" },
      { name: "is_active", type: "boolean", nullable: false, default: "true", description: "Ativo no cardápio" },
      { name: "is_addon", type: "boolean", nullable: false, default: "false", description: "É um adicional?" },
      { name: "addon_category", type: "text", nullable: true, default: null, description: "Categoria de adicional" },
      { name: "barcode", type: "text", nullable: true, default: null, description: "Código de barras" },
      { name: "sku", type: "text", nullable: true, default: null, description: "SKU interno" },
      { name: "production_location_id", type: "uuid", nullable: true, default: null, description: "Local de produção" },
      { name: "available_pdv", type: "boolean", nullable: true, default: "true", description: "Disponível no PDV" },
      { name: "available_table", type: "boolean", nullable: true, default: "true", description: "Disponível mesa" },
      { name: "available_tablet", type: "boolean", nullable: true, default: "true", description: "Disponível tablet" },
      { name: "available_delivery", type: "boolean", nullable: true, default: "true", description: "Disponível delivery" },
      { name: "stock_control", type: "boolean", nullable: true, default: "false", description: "Controla estoque" },
      { name: "current_stock", type: "numeric", nullable: true, default: "0", description: "Estoque atual" },
      { name: "minimum_stock", type: "numeric", nullable: true, default: "0", description: "Estoque mínimo" },
      { name: "sort_order", type: "integer", nullable: true, default: "0", description: "Ordem de exibição" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "category_id", references: "categories.id" },
      { column: "production_location_id", references: "production_locations.id" },
    ],
    rlsPolicies: [
      { name: "Public can view active products", command: "SELECT", using: "is_active = true" },
      { name: "Users can view all products of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage products", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = products.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
  {
    name: "categories",
    description: "Categorias de produtos. Organizam o cardápio em seções.",
    domain: "produtos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome da categoria" },
      { name: "description", type: "text", nullable: true, default: null, description: "Descrição" },
      { name: "icon", type: "text", nullable: true, default: null, description: "Ícone (emoji ou nome)" },
      { name: "color", type: "text", nullable: true, default: null, description: "Cor em hex" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativa" },
      { name: "sort_order", type: "integer", nullable: true, default: "0", description: "Ordem de exibição" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view categories of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage categories", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = categories.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
  {
    name: "combos",
    description: "Combos e promoções. Podem ser: standard (itens fixos), choice (escolha), pizza, rodizio.",
    domain: "produtos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "category_id", type: "uuid", nullable: true, default: null, description: "FK para categoria" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome do combo" },
      { name: "description", type: "text", nullable: true, default: null, description: "Descrição" },
      { name: "price", type: "numeric", nullable: false, default: "0", description: "Preço do combo" },
      { name: "image_url", type: "text", nullable: true, default: null, description: "URL da imagem" },
      { name: "combo_type", type: "text", nullable: false, default: null, description: "standard, choice, pizza, rodizio" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativo" },
      { name: "pizza_max_flavors", type: "integer", nullable: true, default: null, description: "Máx sabores (pizza)" },
      { name: "pizza_pricing_rule", type: "text", nullable: true, default: null, description: "higher, average (pizza)" },
      { name: "rodizio_category_id", type: "uuid", nullable: true, default: null, description: "Categoria do rodízio" },
      { name: "production_location_id", type: "uuid", nullable: true, default: null, description: "Local de produção" },
      { name: "available_pdv", type: "boolean", nullable: true, default: "true", description: "Disponível no PDV" },
      { name: "available_table", type: "boolean", nullable: true, default: "true", description: "Disponível mesa" },
      { name: "available_tablet", type: "boolean", nullable: true, default: "true", description: "Disponível tablet" },
      { name: "available_delivery", type: "boolean", nullable: true, default: "true", description: "Disponível delivery" },
      { name: "requires_notes", type: "boolean", nullable: true, default: "false", description: "Exige observação" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "category_id", references: "categories.id" },
      { column: "rodizio_category_id", references: "categories.id" },
      { column: "production_location_id", references: "production_locations.id" },
    ],
    rlsPolicies: [
      { name: "Public can view active combos", command: "SELECT", using: "is_active = true" },
      { name: "Users can view combos from their restaurant", command: "SELECT", using: "restaurant_id IN (SELECT restaurant_id FROM restaurant_users WHERE user_id = auth.uid())" },
      { name: "Users can manage combos in their restaurant", command: "ALL", using: "restaurant_id IN (SELECT restaurant_id FROM restaurant_users WHERE user_id = auth.uid())" },
    ],
  },
  {
    name: "combo_slots",
    description: "Slots de escolha dentro de combos. Define quantidades mín/máx e preços.",
    domain: "produtos",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "combo_id", type: "uuid", nullable: false, default: null, description: "FK para combo" },
      { name: "slot_type", type: "text", nullable: false, default: null, description: "fixed, category, product" },
      { name: "label", type: "text", nullable: true, default: null, description: "Rótulo do slot" },
      { name: "category_id", type: "uuid", nullable: true, default: null, description: "Categoria permitida" },
      { name: "product_id", type: "uuid", nullable: true, default: null, description: "Produto fixo" },
      { name: "min_quantity", type: "integer", nullable: false, default: "1", description: "Quantidade mínima" },
      { name: "max_quantity", type: "integer", nullable: false, default: "1", description: "Quantidade máxima" },
      { name: "pricing_rule", type: "text", nullable: false, default: "'fixed'", description: "fixed, product_price, higher" },
      { name: "fixed_price", type: "numeric", nullable: true, default: "0", description: "Preço fixo do slot" },
      { name: "is_required", type: "boolean", nullable: true, default: "true", description: "Obrigatório" },
      { name: "display_order", type: "integer", nullable: true, default: "0", description: "Ordem de exibição" },
      { name: "production_location_id", type: "uuid", nullable: true, default: null, description: "Local de produção" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora criação" },
    ],
    foreignKeys: [
      { column: "combo_id", references: "combos.id" },
      { column: "category_id", references: "categories.id" },
      { column: "product_id", references: "products.id" },
      { column: "production_location_id", references: "production_locations.id" },
    ],
    rlsPolicies: [
      { name: "Public can view combo_slots", command: "SELECT", using: "true" },
      { name: "Users can manage combo_slots in their restaurant", command: "ALL", using: "combo_id IN (SELECT id FROM combos WHERE restaurant_id IN (SELECT restaurant_id FROM restaurant_users WHERE user_id = auth.uid()))" },
    ],
  },

  // ========== MESAS/COMANDAS ==========
  {
    name: "tables",
    description: "Mesas do restaurante. Cada mesa pode ter um pedido ativo.",
    domain: "mesas",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "number", type: "text", nullable: false, default: null, description: "Número da mesa" },
      { name: "status", type: "text", nullable: false, default: "'available'", description: "available, occupied, reserved" },
      { name: "capacity", type: "integer", nullable: true, default: "4", description: "Capacidade de pessoas" },
      { name: "current_order_id", type: "uuid", nullable: true, default: null, description: "Pedido ativo" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativa" },
      { name: "position_x", type: "numeric", nullable: true, default: null, description: "Posição X no mapa" },
      { name: "position_y", type: "numeric", nullable: true, default: null, description: "Posição Y no mapa" },
      { name: "nickname", type: "text", nullable: true, default: null, description: "Apelido (ex: 'Varanda')" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "current_order_id", references: "orders.id" },
    ],
    rlsPolicies: [
      { name: "Public can view tables", command: "SELECT", using: "true" },
      { name: "Users can manage tables of their restaurant", command: "ALL", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "commands",
    description: "Comandas do restaurante. Alternativa às mesas para controle individual.",
    domain: "mesas",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "number", type: "text", nullable: false, default: null, description: "Número da comanda" },
      { name: "status", type: "text", nullable: true, default: "'available'", description: "available, in_use" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativa" },
      { name: "current_order_id", type: "uuid", nullable: true, default: null, description: "Pedido ativo" },
      { name: "customer_name", type: "text", nullable: true, default: null, description: "Nome do cliente" },
      { name: "service_tax_enabled", type: "boolean", nullable: true, default: "true", description: "Cobra taxa de serviço" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view commands of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage commands", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = commands.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
      { name: "Restaurant staff can update commands", command: "UPDATE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "command_entries",
    description: "Entradas de comanda (couvert, taxa de entrada). Registra valores cobrados na abertura.",
    domain: "mesas",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "command_id", type: "uuid", nullable: true, default: null, description: "FK para comanda" },
      { name: "order_id", type: "uuid", nullable: true, default: null, description: "FK para pedido" },
      { name: "entry_type_id", type: "uuid", nullable: true, default: null, description: "FK para tipo de entrada" },
      { name: "entry_type_name", type: "text", nullable: false, default: null, description: "Nome do tipo (snapshot)" },
      { name: "entry_mode", type: "entry_mode", nullable: false, default: "'fixed'", description: "fixed, per_person" },
      { name: "amount", type: "numeric", nullable: false, default: null, description: "Valor cobrado" },
      { name: "consumed_amount", type: "numeric", nullable: true, default: "0", description: "Valor consumido" },
      { name: "command_number", type: "text", nullable: false, default: null, description: "Número da comanda" },
      { name: "status", type: "text", nullable: true, default: "'active'", description: "active, closed" },
      { name: "cash_register_id", type: "uuid", nullable: true, default: null, description: "FK para caixa" },
      { name: "opened_by", type: "uuid", nullable: false, default: null, description: "Usuário que abriu" },
      { name: "closed_at", type: "timestamptz", nullable: true, default: null, description: "Data/hora fechamento" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora criação" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "command_id", references: "commands.id" },
      { column: "order_id", references: "orders.id" },
      { column: "entry_type_id", references: "entry_types.id" },
      { column: "cash_register_id", references: "cash_registers.id" },
    ],
    rlsPolicies: [
      { name: "Users can view command entries of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can create command entries for their restaurant", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can update command entries of their restaurant", command: "UPDATE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "entry_types",
    description: "Tipos de entrada (couvert, taxa de show, etc). Define valores padrão.",
    domain: "mesas",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome do tipo" },
      { name: "entry_mode", type: "entry_mode", nullable: false, default: "'fixed'", description: "fixed, per_person" },
      { name: "amount", type: "numeric", nullable: false, default: "0", description: "Valor padrão" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativo" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: true, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view entry types of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage entry types", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = entry_types.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },

  // ========== ESTOQUE ==========
  {
    name: "ingredients",
    description: "Ingredientes para controle de estoque e fichas técnicas.",
    domain: "estoque",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome do ingrediente" },
      { name: "unit", type: "text", nullable: false, default: "'un'", description: "Unidade (kg, lt, un)" },
      { name: "current_stock", type: "numeric", nullable: false, default: "0", description: "Estoque atual" },
      { name: "minimum_stock", type: "numeric", nullable: false, default: "0", description: "Estoque mínimo" },
      { name: "cost_per_unit", type: "numeric", nullable: false, default: "0", description: "Custo por unidade" },
      { name: "is_active", type: "boolean", nullable: false, default: "true", description: "Ativo" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view ingredients of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage ingredients", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = ingredients.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
  {
    name: "product_ingredients",
    description: "Ficha técnica: ingredientes que compõem cada produto.",
    domain: "estoque",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "product_id", type: "uuid", nullable: false, default: null, description: "FK para produto" },
      { name: "ingredient_id", type: "uuid", nullable: false, default: null, description: "FK para ingrediente" },
      { name: "quantity", type: "numeric", nullable: false, default: null, description: "Quantidade usada" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
    ],
    foreignKeys: [
      { column: "product_id", references: "products.id" },
      { column: "ingredient_id", references: "ingredients.id" },
    ],
    rlsPolicies: [
      { name: "Users can view product_ingredients of their restaurant", command: "SELECT", using: "true" },
      { name: "Users can manage product_ingredients", command: "ALL", using: "true" },
    ],
  },
  {
    name: "stock_movements",
    description: "Movimentações de estoque (entradas, saídas, ajustes, vendas).",
    domain: "estoque",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "ingredient_id", type: "uuid", nullable: true, default: null, description: "FK para ingrediente" },
      { name: "product_id", type: "uuid", nullable: true, default: null, description: "FK para produto" },
      { name: "type", type: "text", nullable: false, default: null, description: "entry, exit, adjustment, sale" },
      { name: "quantity", type: "numeric", nullable: false, default: null, description: "Quantidade movimentada" },
      { name: "unit_cost", type: "numeric", nullable: true, default: null, description: "Custo unitário" },
      { name: "total_cost", type: "numeric", nullable: true, default: null, description: "Custo total" },
      { name: "reason", type: "text", nullable: true, default: null, description: "Motivo" },
      { name: "order_id", type: "uuid", nullable: true, default: null, description: "FK para pedido (se venda)" },
      { name: "expense_id", type: "uuid", nullable: true, default: null, description: "FK para despesa (se compra)" },
      { name: "supplier_id", type: "uuid", nullable: true, default: null, description: "FK para fornecedor" },
      { name: "performed_by", type: "uuid", nullable: true, default: null, description: "Usuário que realizou" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "ingredient_id", references: "ingredients.id" },
      { column: "product_id", references: "products.id" },
      { column: "order_id", references: "orders.id" },
      { column: "expense_id", references: "expenses.id" },
      { column: "supplier_id", references: "suppliers.id" },
    ],
    rlsPolicies: [
      { name: "Users can view stock_movements of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can create stock_movements", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },

  // ========== FINANCEIRO ==========
  {
    name: "expenses",
    description: "Despesas do restaurante (contas a pagar, custos operacionais).",
    domain: "financeiro",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "category_id", type: "uuid", nullable: false, default: null, description: "FK para categoria" },
      { name: "supplier_id", type: "uuid", nullable: true, default: null, description: "FK para fornecedor" },
      { name: "title", type: "text", nullable: false, default: null, description: "Título da despesa" },
      { name: "description", type: "text", nullable: true, default: null, description: "Descrição" },
      { name: "amount", type: "numeric", nullable: false, default: null, description: "Valor" },
      { name: "expense_date", type: "date", nullable: false, default: "CURRENT_DATE", description: "Data da despesa" },
      { name: "due_date", type: "date", nullable: true, default: null, description: "Data de vencimento" },
      { name: "paid_at", type: "timestamptz", nullable: true, default: null, description: "Data do pagamento" },
      { name: "status", type: "expense_status", nullable: false, default: "'open'", description: "open, paid, cancelled" },
      { name: "origin", type: "expense_origin", nullable: false, default: "'manual'", description: "manual, stock, cash" },
      { name: "linked_stock_movement_id", type: "uuid", nullable: true, default: null, description: "Movimento de estoque" },
      { name: "linked_cash_movement_id", type: "uuid", nullable: true, default: null, description: "Movimento de caixa" },
      { name: "created_by", type: "uuid", nullable: true, default: null, description: "Usuário que criou" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "category_id", references: "expense_categories.id" },
      { column: "supplier_id", references: "suppliers.id" },
    ],
    rlsPolicies: [
      { name: "expenses_select", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "expenses_insert", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "expenses_update", command: "UPDATE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "expenses_delete", command: "DELETE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "expense_categories",
    description: "Categorias de despesas (aluguel, folha, insumos, etc).",
    domain: "financeiro",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome da categoria" },
      { name: "chart_account_id", type: "uuid", nullable: true, default: null, description: "FK para plano de contas" },
      { name: "is_active", type: "boolean", nullable: false, default: "true", description: "Ativa" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "chart_account_id", references: "chart_of_accounts.id" },
    ],
    rlsPolicies: [
      { name: "expense_categories_select", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "expense_categories_insert", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "expense_categories_update", command: "UPDATE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "expense_categories_delete", command: "DELETE", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "payment_methods",
    description: "Formas de pagamento aceitas (dinheiro, cartão, PIX, etc).",
    domain: "financeiro",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome" },
      { name: "type", type: "text", nullable: false, default: null, description: "cash, credit, debit, pix, voucher" },
      { name: "is_active", type: "boolean", nullable: false, default: "true", description: "Ativa" },
      { name: "requires_change", type: "boolean", nullable: true, default: "false", description: "Requer troco" },
      { name: "icon", type: "text", nullable: true, default: null, description: "Ícone" },
      { name: "sort_order", type: "integer", nullable: true, default: "0", description: "Ordem" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Public can view payment methods", command: "SELECT", using: "true" },
      { name: "Users can manage payment methods", command: "ALL", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "chart_of_accounts",
    description: "Plano de contas para DRE e classificação financeira.",
    domain: "financeiro",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "code", type: "text", nullable: false, default: null, description: "Código (ex: 3.1.01)" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome da conta" },
      { name: "account_type", type: "account_type", nullable: false, default: null, description: "revenue, expense, cost" },
      { name: "parent_id", type: "uuid", nullable: true, default: null, description: "Conta pai (hierarquia)" },
      { name: "is_system", type: "boolean", nullable: true, default: "false", description: "Conta de sistema" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativa" },
      { name: "sort_order", type: "integer", nullable: true, default: "0", description: "Ordem" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: true, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "parent_id", references: "chart_of_accounts.id" },
    ],
    rlsPolicies: [
      { name: "Users can view chart_of_accounts of their restaurants", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can manage chart_of_accounts in their restaurants", command: "ALL", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "suppliers",
    description: "Fornecedores de insumos e serviços.",
    domain: "financeiro",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome/Razão social" },
      { name: "cnpj", type: "text", nullable: true, default: null, description: "CNPJ" },
      { name: "phone", type: "text", nullable: true, default: null, description: "Telefone" },
      { name: "email", type: "text", nullable: true, default: null, description: "Email" },
      { name: "address", type: "text", nullable: true, default: null, description: "Endereço" },
      { name: "contact_name", type: "text", nullable: true, default: null, description: "Contato" },
      { name: "notes", type: "text", nullable: true, default: null, description: "Observações" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativo" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: true, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view suppliers of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can manage suppliers", command: "ALL", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },

  // ========== FISCAL ==========
  {
    name: "fiscal_configs",
    description: "Configurações fiscais do restaurante (certificado, CSC, ACBr).",
    domain: "fiscal",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "inscricao_estadual", type: "text", nullable: true, default: null, description: "Inscrição Estadual" },
      { name: "regime_tributario", type: "text", nullable: true, default: "'simples_nacional'", description: "Regime tributário" },
      { name: "certificate_uploaded", type: "boolean", nullable: true, default: "false", description: "Certificado enviado" },
      { name: "certificate_password", type: "text", nullable: true, default: null, description: "Senha do certificado" },
      { name: "certificate_expires_at", type: "timestamptz", nullable: true, default: null, description: "Validade certificado" },
      { name: "csc_id", type: "text", nullable: true, default: null, description: "ID do CSC" },
      { name: "csc_token", type: "text", nullable: true, default: null, description: "Token do CSC" },
      { name: "nfce_serie", type: "integer", nullable: true, default: "1", description: "Série da NFC-e" },
      { name: "nfce_numero_atual", type: "integer", nullable: true, default: "0", description: "Último número" },
      { name: "ambiente", type: "text", nullable: true, default: "'homologacao'", description: "homologacao, producao" },
      { name: "integrator", type: "text", nullable: true, default: null, description: "Integrador (acbr)" },
      { name: "acbr_host", type: "text", nullable: true, default: "'localhost'", description: "Host do ACBr" },
      { name: "acbr_port", type: "integer", nullable: true, default: "3434", description: "Porta do ACBr" },
      { name: "acbr_timeout_ms", type: "integer", nullable: true, default: "30000", description: "Timeout em ms" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: true, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Managers can manage fiscal configs", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = fiscal_configs.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
  {
    name: "nfce_issued",
    description: "NFC-e emitidas. Histórico de notas fiscais.",
    domain: "fiscal",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "order_id", type: "uuid", nullable: true, default: null, description: "FK para pedido" },
      { name: "numero", type: "integer", nullable: false, default: null, description: "Número da NFC-e" },
      { name: "serie", type: "integer", nullable: false, default: null, description: "Série" },
      { name: "chave_acesso", type: "text", nullable: true, default: null, description: "Chave de acesso (44 dígitos)" },
      { name: "protocolo", type: "text", nullable: true, default: null, description: "Protocolo de autorização" },
      { name: "status", type: "text", nullable: true, default: "'pending'", description: "pending, authorized, rejected, cancelled" },
      { name: "status_message", type: "text", nullable: true, default: null, description: "Mensagem de retorno" },
      { name: "valor_total", type: "numeric", nullable: false, default: null, description: "Valor total" },
      { name: "xml_enviado", type: "text", nullable: true, default: null, description: "XML enviado" },
      { name: "xml_retorno", type: "text", nullable: true, default: null, description: "XML de retorno" },
      { name: "qrcode_url", type: "text", nullable: true, default: null, description: "URL do QR Code" },
      { name: "danfe_url", type: "text", nullable: true, default: null, description: "URL do DANFE" },
      { name: "is_contingency", type: "boolean", nullable: true, default: "false", description: "Emitida em contingência" },
      { name: "contingency_reason", type: "text", nullable: true, default: null, description: "Motivo contingência" },
      { name: "cancelled_at", type: "timestamptz", nullable: true, default: null, description: "Data cancelamento" },
      { name: "cancel_protocol", type: "text", nullable: true, default: null, description: "Protocolo cancelamento" },
      { name: "cancel_reason", type: "text", nullable: true, default: null, description: "Motivo cancelamento" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora emissão" },
      { name: "updated_at", type: "timestamptz", nullable: true, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "order_id", references: "orders.id" },
    ],
    rlsPolicies: [
      { name: "Users can view nfce", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage nfce", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = nfce_issued.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
  {
    name: "products_fiscal",
    description: "Dados fiscais dos produtos (NCM, CFOP, alíquotas).",
    domain: "fiscal",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "product_id", type: "uuid", nullable: false, default: null, description: "FK para produto" },
      { name: "ncm", type: "text", nullable: true, default: null, description: "NCM (8 dígitos)" },
      { name: "cfop", type: "text", nullable: true, default: "'5102'", description: "CFOP" },
      { name: "cst_icms", type: "text", nullable: true, default: null, description: "CST ICMS" },
      { name: "cst_pis", type: "text", nullable: true, default: null, description: "CST PIS" },
      { name: "cst_cofins", type: "text", nullable: true, default: null, description: "CST COFINS" },
      { name: "aliquota_icms", type: "numeric", nullable: true, default: "0", description: "Alíquota ICMS %" },
      { name: "aliquota_pis", type: "numeric", nullable: true, default: "0", description: "Alíquota PIS %" },
      { name: "aliquota_cofins", type: "numeric", nullable: true, default: "0", description: "Alíquota COFINS %" },
      { name: "cest", type: "text", nullable: true, default: null, description: "CEST" },
      { name: "origem", type: "text", nullable: true, default: "'0'", description: "Origem (0-8)" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: true, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "product_id", references: "products.id" },
    ],
    rlsPolicies: [
      { name: "Users can view products_fiscal", command: "SELECT", using: "true" },
      { name: "Users can manage products_fiscal", command: "ALL", using: "true" },
    ],
  },

  // ========== USUÁRIOS ==========
  {
    name: "restaurant_users",
    description: "Relacionamento usuários x restaurantes. Define permissões.",
    domain: "usuarios",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "user_id", type: "uuid", nullable: false, default: null, description: "FK para auth.users" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "role", type: "app_role", nullable: false, default: "'cashier'", description: "super_admin, restaurant_owner, manager, cashier, waiter, kitchen" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativo" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view their own memberships", command: "SELECT", using: "user_id = auth.uid()" },
      { name: "Managers can view all memberships", command: "SELECT", using: "EXISTS(SELECT 1 FROM restaurant_users ru WHERE ru.restaurant_id = restaurant_users.restaurant_id AND ru.user_id = auth.uid() AND ru.role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
  {
    name: "profiles",
    description: "Perfis de usuários com dados adicionais.",
    domain: "usuarios",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: null, description: "PK = auth.users.id" },
      { name: "email", type: "text", nullable: true, default: null, description: "Email" },
      { name: "full_name", type: "text", nullable: true, default: null, description: "Nome completo" },
      { name: "avatar_url", type: "text", nullable: true, default: null, description: "URL do avatar" },
      { name: "phone", type: "text", nullable: true, default: null, description: "Telefone" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [],
    rlsPolicies: [
      { name: "Users can view their own profile", command: "SELECT", using: "id = auth.uid()" },
      { name: "Users can update their own profile", command: "UPDATE", using: "id = auth.uid()" },
    ],
  },
  {
    name: "waiter_sessions",
    description: "Sessões de trabalho dos garçons. Controla check-in/check-out.",
    domain: "usuarios",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "waiter_id", type: "uuid", nullable: false, default: null, description: "FK para usuário" },
      { name: "waiter_name", type: "text", nullable: false, default: null, description: "Nome do garçom" },
      { name: "status", type: "text", nullable: false, default: "'active'", description: "active, ended" },
      { name: "checked_in_at", type: "timestamptz", nullable: false, default: "now()", description: "Check-in" },
      { name: "checked_out_at", type: "timestamptz", nullable: true, default: null, description: "Check-out" },
      { name: "total_orders", type: "integer", nullable: true, default: "0", description: "Total de pedidos" },
      { name: "total_sales", type: "numeric", nullable: true, default: "0", description: "Total de vendas" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view waiter_sessions", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can manage waiter_sessions", command: "ALL", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },

  // ========== CONFIGURAÇÕES ==========
  {
    name: "restaurants",
    description: "Restaurantes cadastrados na plataforma.",
    domain: "config",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome do restaurante" },
      { name: "slug", type: "text", nullable: true, default: null, description: "Slug para URL" },
      { name: "cnpj", type: "text", nullable: true, default: null, description: "CNPJ" },
      { name: "phone", type: "text", nullable: true, default: null, description: "Telefone" },
      { name: "email", type: "text", nullable: true, default: null, description: "Email" },
      { name: "address", type: "text", nullable: true, default: null, description: "Endereço completo" },
      { name: "city", type: "text", nullable: true, default: null, description: "Cidade" },
      { name: "state", type: "text", nullable: true, default: null, description: "Estado (UF)" },
      { name: "postal_code", type: "text", nullable: true, default: null, description: "CEP" },
      { name: "logo_url", type: "text", nullable: true, default: null, description: "URL do logo" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativo" },
      { name: "timezone", type: "text", nullable: true, default: "'America/Sao_Paulo'", description: "Fuso horário" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [],
    rlsPolicies: [
      { name: "Public can view active restaurants", command: "SELECT", using: "is_active = true" },
      { name: "Users can view their restaurants", command: "SELECT", using: "id IN (SELECT restaurant_id FROM restaurant_users WHERE user_id = auth.uid())" },
      { name: "Owners can update their restaurants", command: "UPDATE", using: "id IN (SELECT restaurant_id FROM restaurant_users WHERE user_id = auth.uid() AND role IN ('restaurant_owner', 'super_admin'))" },
    ],
  },
  {
    name: "restaurant_settings",
    description: "Configurações diversas do restaurante (JSON flexível).",
    domain: "config",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "key", type: "text", nullable: false, default: null, description: "Chave da configuração" },
      { name: "value", type: "jsonb", nullable: true, default: null, description: "Valor (JSON)" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view settings of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Users can manage settings", command: "ALL", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "printers",
    description: "Impressoras configuradas para impressão de pedidos.",
    domain: "config",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome da impressora" },
      { name: "printer_name", type: "text", nullable: false, default: null, description: "Nome no SO" },
      { name: "type", type: "text", nullable: false, default: "'thermal'", description: "thermal, label, a4" },
      { name: "connection_type", type: "text", nullable: false, default: "'usb'", description: "usb, network, bluetooth" },
      { name: "ip_address", type: "text", nullable: true, default: null, description: "IP (se rede)" },
      { name: "port", type: "integer", nullable: true, default: null, description: "Porta (se rede)" },
      { name: "paper_width", type: "integer", nullable: true, default: "80", description: "Largura papel (mm)" },
      { name: "is_default", type: "boolean", nullable: true, default: "false", description: "Impressora padrão" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativa" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view printers of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage printers", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = printers.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
  {
    name: "production_locations",
    description: "Locais de produção (cozinha, bar, etc). Roteiam pedidos.",
    domain: "config",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome do local" },
      { name: "printer_id", type: "uuid", nullable: true, default: null, description: "Impressora associada" },
      { name: "color", type: "text", nullable: true, default: null, description: "Cor para identificação" },
      { name: "is_active", type: "boolean", nullable: true, default: "true", description: "Ativo" },
      { name: "sort_order", type: "integer", nullable: true, default: "0", description: "Ordem" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: false, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "printer_id", references: "printers.id" },
    ],
    rlsPolicies: [
      { name: "Users can view production_locations of their restaurant", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage production_locations", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = production_locations.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },

  // ========== OUTROS ==========
  {
    name: "customers",
    description: "Clientes cadastrados para delivery e fidelização.",
    domain: "outros",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "name", type: "text", nullable: false, default: null, description: "Nome" },
      { name: "phone", type: "text", nullable: true, default: null, description: "Telefone" },
      { name: "email", type: "text", nullable: true, default: null, description: "Email" },
      { name: "address", type: "text", nullable: true, default: null, description: "Endereço" },
      { name: "city", type: "text", nullable: true, default: null, description: "Cidade" },
      { name: "total_orders", type: "integer", nullable: true, default: "0", description: "Total de pedidos" },
      { name: "total_spent", type: "numeric", nullable: true, default: "0", description: "Total gasto" },
      { name: "last_order_at", type: "timestamptz", nullable: true, default: null, description: "Último pedido" },
      { name: "accept_marketing", type: "boolean", nullable: true, default: "false", description: "Aceita marketing" },
      { name: "source", type: "text", nullable: true, default: "'manual'", description: "Origem do cadastro" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora criação" },
      { name: "updated_at", type: "timestamptz", nullable: true, default: "now()", description: "Última atualização" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Staff can view customers", command: "SELECT", using: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can manage customers", command: "ALL", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = customers.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
  {
    name: "customer_ratings",
    description: "Avaliações dos clientes (NPS, qualidade, atendimento).",
    domain: "outros",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "order_id", type: "uuid", nullable: true, default: null, description: "FK para pedido" },
      { name: "table_number", type: "text", nullable: true, default: null, description: "Número da mesa" },
      { name: "food_quality", type: "integer", nullable: true, default: null, description: "Qualidade comida (1-5)" },
      { name: "service_quality", type: "integer", nullable: true, default: null, description: "Qualidade atendimento (1-5)" },
      { name: "environment", type: "integer", nullable: true, default: null, description: "Ambiente (1-5)" },
      { name: "value_for_money", type: "integer", nullable: true, default: null, description: "Custo-benefício (1-5)" },
      { name: "overall_experience", type: "integer", nullable: true, default: null, description: "Experiência geral (1-5)" },
      { name: "comments", type: "text", nullable: true, default: null, description: "Comentários" },
      { name: "customer_name", type: "text", nullable: true, default: null, description: "Nome do cliente" },
      { name: "customer_email", type: "text", nullable: true, default: null, description: "Email" },
      { name: "customer_phone", type: "text", nullable: true, default: null, description: "Telefone" },
      { name: "accept_marketing", type: "boolean", nullable: true, default: "false", description: "Aceita marketing" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
      { column: "order_id", references: "orders.id" },
    ],
    rlsPolicies: [
      { name: "Anyone can submit ratings", command: "INSERT", withCheck: "EXISTS(SELECT 1 FROM restaurants r WHERE r.id = customer_ratings.restaurant_id AND COALESCE(r.is_active, true) = true)" },
      { name: "Restaurant users can view ratings", command: "SELECT", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = customer_ratings.restaurant_id AND user_id = auth.uid())" },
    ],
  },
  {
    name: "notifications",
    description: "Notificações internas do sistema.",
    domain: "outros",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "user_id", type: "uuid", nullable: false, default: null, description: "Usuário destinatário" },
      { name: "type", type: "text", nullable: false, default: null, description: "Tipo da notificação" },
      { name: "title", type: "text", nullable: false, default: null, description: "Título" },
      { name: "message", type: "text", nullable: false, default: null, description: "Mensagem" },
      { name: "data", type: "jsonb", nullable: true, default: "'{}'", description: "Dados adicionais" },
      { name: "is_read", type: "boolean", nullable: false, default: "false", description: "Foi lida" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can view their own notifications", command: "SELECT", using: "user_id = auth.uid()" },
      { name: "Users can update their own notifications", command: "UPDATE", using: "user_id = auth.uid()" },
      { name: "System can create notifications for restaurant users", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
    ],
  },
  {
    name: "error_logs",
    description: "Logs de erros do sistema para debug.",
    domain: "outros",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: true, default: null, description: "FK para restaurante" },
      { name: "user_id", type: "uuid", nullable: true, default: null, description: "Usuário" },
      { name: "source", type: "text", nullable: false, default: null, description: "Origem (pdv, tablet, dashboard)" },
      { name: "error_code", type: "text", nullable: true, default: null, description: "Código do erro" },
      { name: "error_message", type: "text", nullable: false, default: null, description: "Mensagem" },
      { name: "error_stack", type: "text", nullable: true, default: null, description: "Stack trace" },
      { name: "context", type: "jsonb", nullable: true, default: null, description: "Contexto adicional" },
      { name: "url", type: "text", nullable: true, default: null, description: "URL onde ocorreu" },
      { name: "user_agent", type: "text", nullable: true, default: null, description: "User agent" },
      { name: "created_at", type: "timestamptz", nullable: true, default: "now()", description: "Data/hora" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Anyone can insert error logs", command: "INSERT", withCheck: "source IS NOT NULL AND source <> '' AND error_message IS NOT NULL AND error_message <> ''" },
      { name: "Restaurant members can view their errors", command: "SELECT", using: "restaurant_id IN (SELECT restaurant_id FROM restaurant_users WHERE user_id = auth.uid())" },
    ],
  },
  {
    name: "operation_logs",
    description: "Logs de operações sensíveis (auditoria).",
    domain: "outros",
    columns: [
      { name: "id", type: "uuid", nullable: false, default: "gen_random_uuid()", description: "Identificador único" },
      { name: "restaurant_id", type: "uuid", nullable: false, default: null, description: "FK para restaurante" },
      { name: "user_id", type: "uuid", nullable: false, default: null, description: "Usuário que realizou" },
      { name: "action", type: "text", nullable: false, default: null, description: "Ação realizada" },
      { name: "entity_type", type: "text", nullable: false, default: null, description: "Tipo da entidade" },
      { name: "entity_id", type: "uuid", nullable: true, default: null, description: "ID da entidade" },
      { name: "details", type: "jsonb", nullable: true, default: null, description: "Detalhes da operação" },
      { name: "ip_address", type: "text", nullable: true, default: null, description: "IP de origem" },
      { name: "created_at", type: "timestamptz", nullable: false, default: "now()", description: "Data/hora" },
    ],
    foreignKeys: [
      { column: "restaurant_id", references: "restaurants.id" },
    ],
    rlsPolicies: [
      { name: "Users can create operation logs for their restaurant", command: "INSERT", withCheck: "user_belongs_to_restaurant(auth.uid(), restaurant_id)" },
      { name: "Managers can view operation logs", command: "SELECT", using: "EXISTS(SELECT 1 FROM restaurant_users WHERE restaurant_id = operation_logs.restaurant_id AND user_id = auth.uid() AND role IN ('restaurant_owner', 'manager', 'super_admin'))" },
    ],
  },
];

// Helper para buscar tabelas por domínio
export function getTablesByDomain(domainId: string): TableSchema[] {
  return DATABASE_TABLES.filter(t => t.domain === domainId);
}

// Helper para buscar uma tabela específica
export function getTableByName(name: string): TableSchema | undefined {
  return DATABASE_TABLES.find(t => t.name === name);
}
